import React from "react";
import "../../Styles/LeaveScreen.css";

import { apiDelete, apiPost } from "../../Api/Client.js";
import { Panel, SimpleTable, StatusPill } from "../../Shared/ScreenComponents.jsx";
import { employeeName, findById, formatDate, isJuniorType } from "../../Shared/ScreenUtils.jsx";

// Leave type is derived from employment status now (Junior -> Unpaid, else
// Paid) rather than chosen — so a reviewer only needs to see the one label
// that carries both facts, not two columns that always agree with each other.
function employmentPayLabel(employee) {
  return isJuniorType(employee?.employment_type) ? "Junior (Unpaid)" : "Full-Time (Paid)";
}

// One date column, not two. A leave is a span; splitting it across "Starts" and
// "Ends" made the reader join them back up, and a single-day leave printed the
// same date twice.
function dateSpan(from, to) {
  const a = formatDate(from);
  const b = formatDate(to);
  return a === b ? a : `${a} → ${b}`;
}

function projectFor(data, employeeId) {
  const assignment = (data.teamAssignments || []).find(
    (t) => String(t.employee || t.employee_id) === String(employeeId)
  );
  if (!assignment) return "";
  const project = findById(data.projects, assignment.project || assignment.project_id);
  return project?.name || project?.title || "";
}

export function LeaveRequestsScreen({ data, reload }) {
  const user = data.me?.user || data.me?.account || data.me || {};
  const isSuperAdmin = Boolean(user.is_superuser || user.isSuperuser || user.is_staff || user.isStaff);
  const currentEmployeeId = data.me?.employees?.[0]?.id;
  
  const allLeaveRows = data.leaveOverview?.results?.length ? data.leaveOverview.results : data.leaveRequests || [];
  
  const isDirectReportLeave = (item) => (data.employees || []).some((employee) => (
    String(employee.id) === String(item.employee || item.employee_id)
    && String(employee.manager || employee.manager_id) === String(currentEmployeeId)
  ));
  
  const canReviewLeave = (item) => isSuperAdmin || (isDirectReportLeave(item) && String(item.employee || item.employee_id) !== String(currentEmployeeId));
  const hasDirectReports = (data.employees || []).some(emp => String(emp.manager || emp.manager_id) === String(currentEmployeeId));

  if (!isSuperAdmin && !hasDirectReports) {
    return (
      <section className="Leave-Screen Screen-Stack">
        <section className="Page-Heading"><div><span>Leave</span><h1>Leave Console</h1></div></section>
        <div style={{ textAlign: "center", padding: "48px 24px", color: "var(--c-500)" }}>
          <h2>Access Denied</h2>
          <p>You Must Be An HR Admin Or A Department Manager To View This Page.</p>
        </div>
      </section>
    );
  }

  const leaveRows = isSuperAdmin
    ? allLeaveRows
    : allLeaveRows.filter((item) => canReviewLeave(item) || isDirectReportLeave(item));

  const reviewLeave = async (id, action) => {
    if (!id) return;
    try {
      await apiPost(`/MainApp/LeaveRequests/${id}/${action}/`, action === "reject" ? { reason: "Rejected From Leave Console" } : {});
      reload(["leaveRequests", "leaveBalances", "leaveOverview", "notifications", "employees"]);
    } catch (err) {
      // A refused approve or reject (not their report, balance, expired session) must be seen, or the
      // manager believes it went through while the request stays pending.
      alert(err?.payload?.detail || err?.message || "Failed To Review Leave Request.");
    }
  };

  const deleteLeave = async (id) => {
    if (!id) return;
    if (!window.confirm("Are You Sure You Want To Delete This Leave Request?")) return;
    try {
      await apiDelete(`/MainApp/LeaveRequests/${id}/`);
      reload(["leaveRequests", "leaveBalances", "leaveOverview", "notifications", "employees"]);
    } catch (err) {
      alert(err?.payload?.detail || err?.message || "Failed To Delete Leave Request.");
    }
  };

  const leaveTone = (status = "") => {
    const value = String(status).toLowerCase();
    if (["approved", "completed", "done"].includes(value)) return "green";
    if (["rejected", "expired"].includes(value)) return "red";
    return "gold";
  };

  const requestColumns = ["Employee", "Department", "Project", "Type", "Dates", "Days", "Status", "Action"];
  const requestRows = leaveRows.map((item) => {
    const employee = findById(data.employees, item.employee || item.employee_id);
    const base = [
      item.employee_name || employeeName(data, item.employee || item.employee_id),
      employee?.department_name || "—",
      projectFor(data, item.employee || item.employee_id) || "—",
      employmentPayLabel(employee),
      dateSpan(item.starts_on, item.ends_on),
      Math.floor(item.requested_days || 0),
      <StatusPill key="status" tone={leaveTone(item.status)}>{item.status}</StatusPill>
    ];
    base.push(
      <span className="Table-Actions" key="actions">
        {canReviewLeave(item) && <button className="Soft-Button Small" onClick={() => reviewLeave(item.id, "approve")} disabled={String(item.status).toLowerCase() === "approved"}>Approve</button>}
        {canReviewLeave(item) && <button className="Soft-Button Small Danger" onClick={() => reviewLeave(item.id, "reject")} disabled={String(item.status).toLowerCase() === "rejected"}>Reject</button>}
        {canReviewLeave(item) && <button className="Soft-Button Small Danger" onClick={() => deleteLeave(item.id)}>Delete</button>}
      </span>
    );
    return base;
  });

  return (
    <section className="Leave-Screen Screen-Stack">
      <section className="Page-Heading">
        <div><span>Leave</span><h1>Leave Console</h1></div>
        <StatusPill tone="gold">{leaveRows.filter((item) => ["submitted", "pending"].includes(String(item.status).toLowerCase())).length} Pending</StatusPill>
      </section>
      <Panel title="Leave Requests" subtitle="Review And Approve Team Leave Requests.">
        <SimpleTable columns={requestColumns} rows={requestRows} />
      </Panel>
    </section>
  );
}
